/**
 * WebhookFormModal - Create or edit an event-notification webhook
 *
 * Features:
 * - Name, description, HTTPS endpoint URL
 * - Event subscription grouped by category with category-level toggles
 * - Status toggle (edit mode)
 * - On create, shows the signing secret ONCE
 */

import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Button,
  Input,
  Label,
  Checkbox,
  Badge,
  Alert,
  AlertDescription,
  Spinner,
  Textarea,
} from '@/components/ui';
import { Webhook as WebhookIcon, Copy, Check, AlertTriangle, Zap } from 'lucide-react';
import {
  createWebhook,
  updateWebhook,
  WebhookListItem,
} from '@/services/webhookService';
import {
  WEBHOOK_EVENT_DEFINITIONS,
  WebhookEventType,
  WebhookEventCategory,
} from '@simcheck/shared-types';

interface WebhookFormModalProps {
  isOpen: boolean;
  onClose: () => void;
  organizationId: string;
  /** Pass a webhook to edit; omit to create. */
  webhook?: WebhookListItem | null;
  onSuccess?: () => void;
}

type Step = 'form' | 'success';

/**
 * Events that the backend actually emits today. The full catalogue is defined
 * (and selectable) so subscriptions are forward-compatible, but only these
 * fire `triggerEvent` right now — surfaced so users aren't surprised by silence.
 * Keep in sync with `WebhookService.triggerEvent` call sites.
 */
const LIVE_EVENTS = new Set<WebhookEventType>([
  'execution.artifact_ready',
  'report.digest_sent',
  'report.export_ready',
]);

const CATEGORY_LABELS: Record<WebhookEventCategory, string> = {
  execution: 'Execution',
  test_run: 'Test Runs',
  schedule: 'Schedules',
  device: 'Devices',
  modem: 'Modems',
  esim: 'eSIM',
  batch: 'Batch',
  artifact: 'Artifacts & SSI',
  report: 'Reports',
};

export function WebhookFormModal({
  isOpen,
  onClose,
  organizationId,
  webhook,
  onSuccess,
}: WebhookFormModalProps) {
  const isEdit = !!webhook;

  const [step, setStep] = useState<Step>('form');

  // Form state
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [url, setUrl] = useState('');
  const [selectedEvents, setSelectedEvents] = useState<WebhookEventType[]>([]);

  // UI state
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [generatedSecret, setGeneratedSecret] = useState('');
  const [copied, setCopied] = useState(false);

  // Hydrate form when opening (edit) / reset (create)
  useEffect(() => {
    if (!isOpen) return;
    setStep('form');
    setError('');
    setGeneratedSecret('');
    setCopied(false);
    if (webhook) {
      setName(webhook.name);
      setDescription(webhook.description || '');
      setUrl(webhook.url);
      setSelectedEvents(webhook.events);
    } else {
      setName('');
      setDescription('');
      setUrl('');
      setSelectedEvents([]);
    }
  }, [isOpen, webhook]);

  // Categories in declaration order
  const categories = useMemo(
    () => Array.from(new Set(WEBHOOK_EVENT_DEFINITIONS.map((d) => d.category))),
    []
  );

  const handleEventToggle = useCallback((event: WebhookEventType) => {
    setSelectedEvents((prev) =>
      prev.includes(event) ? prev.filter((e) => e !== event) : [...prev, event]
    );
  }, []);

  const handleCategoryToggle = useCallback(
    (category: WebhookEventCategory) => {
      const categoryEvents = WEBHOOK_EVENT_DEFINITIONS.filter(
        (d) => d.category === category
      ).map((d) => d.event);
      setSelectedEvents((prev) => {
        const allSelected = categoryEvents.every((e) => prev.includes(e));
        return allSelected
          ? prev.filter((e) => !categoryEvents.includes(e))
          : [...new Set([...prev, ...categoryEvents])];
      });
    },
    []
  );

  const validate = (): string | null => {
    if (!name.trim()) return 'Name is required';
    if (!url.trim()) return 'Endpoint URL is required';
    if (!url.trim().startsWith('https://')) return 'Endpoint URL must use HTTPS';
    if (selectedEvents.length === 0) return 'Select at least one event';
    return null;
  };

  const handleSubmit = async () => {
    const validationError = validate();
    if (validationError) {
      setError(validationError);
      return;
    }

    setLoading(true);
    setError('');

    try {
      if (isEdit && webhook) {
        await updateWebhook(
          webhook.id,
          {
            name: name.trim(),
            description: description.trim() || undefined,
            url: url.trim(),
            events: selectedEvents,
          },
          organizationId
        );
        onSuccess?.();
        handleClose();
      } else {
        const response = await createWebhook(
          {
            name: name.trim(),
            description: description.trim() || undefined,
            url: url.trim(),
            events: selectedEvents,
          },
          organizationId
        );
        setGeneratedSecret(response.secret);
        setStep('success');
        onSuccess?.();
      }
    } catch (err: any) {
      console.error('Error saving webhook:', err);
      setError(err.message || 'Failed to save webhook');
    } finally {
      setLoading(false);
    }
  };

  const handleCopy = useCallback(async () => {
    try {
      await navigator.clipboard.writeText(generatedSecret);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  }, [generatedSecret]);

  const handleClose = () => {
    setStep('form');
    setName('');
    setDescription('');
    setUrl('');
    setSelectedEvents([]);
    setError('');
    setGeneratedSecret('');
    setCopied(false);
    onClose();
  };

  const renderForm = () => (
    <>
      <DialogHeader>
        <DialogTitle className="flex items-center gap-2">
          <WebhookIcon className="w-5 h-5" />
          {isEdit ? 'Edit Webhook' : 'Create Webhook'}
        </DialogTitle>
        <DialogDescription>
          {isEdit
            ? 'Update this endpoint and the events it receives.'
            : 'Send a signed HTTPS POST to your endpoint when events occur.'}
        </DialogDescription>
      </DialogHeader>

      <div className="space-y-6 py-4 max-h-[60vh] overflow-y-auto">
        {error && (
          <Alert variant="destructive">
            <AlertTriangle className="h-4 w-4" />
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}

        {/* Name */}
        <div className="space-y-2">
          <Label htmlFor="wh-name">Name *</Label>
          <Input
            id="wh-name"
            placeholder="e.g., CI/CD Notifications"
            value={name}
            onChange={(e) => setName(e.target.value)}
            disabled={loading}
          />
        </div>

        {/* URL */}
        <div className="space-y-2">
          <Label htmlFor="wh-url">Endpoint URL *</Label>
          <Input
            id="wh-url"
            placeholder="https://example.com/webhooks/simcheck"
            value={url}
            onChange={(e) => setUrl(e.target.value)}
            disabled={loading}
          />
          <p className="text-xs text-muted-foreground">
            Must be HTTPS. Each delivery is signed with HMAC-SHA256 via the{' '}
            <code className="font-mono">X-Webhook-Signature</code> header.
          </p>
        </div>

        {/* Description */}
        <div className="space-y-2">
          <Label htmlFor="wh-desc">Description</Label>
          <Textarea
            id="wh-desc"
            placeholder="Optional description..."
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            disabled={loading}
            rows={2}
          />
        </div>

        {/* Events */}
        <div className="space-y-3">
          <div>
            <Label className="flex items-center gap-2">
              <Zap className="w-4 h-4" />
              Events *
            </Label>
            <p className="text-xs text-muted-foreground mt-1">
              Choose which events to receive.{' '}
              <span className="inline-flex items-center gap-1">
                The <Badge variant="outline" className="text-[10px] px-1 py-0">Live</Badge> tag marks
                events that fire today; others are reserved for upcoming triggers.
              </span>
            </p>
          </div>

          <div className="space-y-4 border rounded-lg p-4 bg-muted/30">
            {categories.map((category) => {
              const defs = WEBHOOK_EVENT_DEFINITIONS.filter((d) => d.category === category);
              const events = defs.map((d) => d.event);
              const selectedInCategory = events.filter((e) => selectedEvents.includes(e));
              const allSelected = events.length > 0 && selectedInCategory.length === events.length;

              return (
                <div key={category} className="space-y-2">
                  <div className="flex items-center gap-2">
                    <Checkbox
                      id={`wh-cat-${category}`}
                      checked={allSelected}
                      onCheckedChange={() => handleCategoryToggle(category)}
                      disabled={loading}
                    />
                    <Label
                      htmlFor={`wh-cat-${category}`}
                      className="font-medium cursor-pointer"
                    >
                      {CATEGORY_LABELS[category] || category}
                    </Label>
                    {selectedInCategory.length > 0 && (
                      <Badge variant="secondary" className="text-xs">
                        {selectedInCategory.length}/{events.length}
                      </Badge>
                    )}
                  </div>

                  <div className="ml-6 grid grid-cols-1 sm:grid-cols-2 gap-2">
                    {defs.map((def) => (
                      <div key={def.event} className="flex items-start gap-2">
                        <Checkbox
                          id={`wh-evt-${def.event}`}
                          checked={selectedEvents.includes(def.event)}
                          onCheckedChange={() => handleEventToggle(def.event)}
                          disabled={loading}
                        />
                        <div className="flex-1">
                          <Label
                            htmlFor={`wh-evt-${def.event}`}
                            className="text-sm cursor-pointer flex items-center gap-1 flex-wrap"
                          >
                            {def.displayName}
                            {LIVE_EVENTS.has(def.event) && (
                              <Badge
                                variant="outline"
                                className="text-[10px] px-1 py-0 text-status-success border-status-success/40"
                              >
                                Live
                              </Badge>
                            )}
                          </Label>
                          <p className="text-xs text-muted-foreground font-mono">{def.event}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      <DialogFooter>
        <Button variant="outline" onClick={handleClose} disabled={loading}>
          Cancel
        </Button>
        <Button onClick={handleSubmit} disabled={loading}>
          {loading ? (
            <>
              <Spinner size="sm" className="mr-2" />
              {isEdit ? 'Saving...' : 'Creating...'}
            </>
          ) : (
            <>
              <WebhookIcon className="w-4 h-4 mr-2" />
              {isEdit ? 'Save Changes' : 'Create Webhook'}
            </>
          )}
        </Button>
      </DialogFooter>
    </>
  );

  const renderSuccess = () => (
    <>
      <DialogHeader>
        <DialogTitle className="flex items-center gap-2 text-status-success">
          <Check className="w-5 h-5" />
          Webhook Created
        </DialogTitle>
        <DialogDescription>Your webhook is active and ready to receive events.</DialogDescription>
      </DialogHeader>

      <div className="space-y-6 py-4">
        <Alert className="border-status-warning bg-status-warning-bg">
          <AlertTriangle className="h-4 w-4 text-status-warning" />
          <AlertDescription className="text-status-warning">
            <strong>Important:</strong> Copy your signing secret now. You won't be able to see it again!
          </AlertDescription>
        </Alert>

        <div className="space-y-2">
          <Label>Signing Secret</Label>
          <div className="flex items-center gap-2">
            <div className="flex-1 p-3 bg-muted rounded-lg font-mono text-sm break-all border">
              {generatedSecret}
            </div>
            <Button variant="outline" size="icon" onClick={handleCopy} className="shrink-0">
              {copied ? (
                <Check className="w-4 h-4 text-status-success" />
              ) : (
                <Copy className="w-4 h-4" />
              )}
            </Button>
          </div>
          <p className="text-xs text-muted-foreground">
            Use this secret to verify the <code className="font-mono">X-Webhook-Signature</code>{' '}
            header (HMAC-SHA256 of the raw request body).
          </p>
        </div>
      </div>

      <DialogFooter>
        <Button onClick={handleClose}>Done</Button>
      </DialogFooter>
    </>
  );

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && handleClose()}>
      <DialogContent className="max-w-2xl">
        {step === 'form' ? renderForm() : renderSuccess()}
      </DialogContent>
    </Dialog>
  );
}

export default WebhookFormModal;
